import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateCSRFToken, validateCSRFToken } from './auth-utils';
import axios from 'axios';

// Création du contexte de sécurité
const SecurityContext = createContext();

// Hook personnalisé pour utiliser le contexte de sécurité
export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity doit être utilisé à l\'intérieur d\'un SecurityProvider');
  }
  return context;
};

// Composant Provider qui va fournir le contexte à l'application
export const SecurityProvider = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  
  // Générer un nouveau jeton CSRF au chargement du provider
  useEffect(() => {
    const token = generateCSRFToken();
    setCsrfToken(token);
    
    // Vérifier l'état d'authentification au chargement
    const storedUser = localStorage.getItem("user");
    const loggedUntil = sessionStorage.getItem("logged");
    
    if (storedUser && loggedUntil) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (Array.isArray(parsedUser) && parsedUser.length > 0 && parsedUser[0].id) {
          setUser(parsedUser[0]);
          setIsAuthenticated(true);
        }
      } catch (e) {
        console.error('Erreur parsing user:', e);
      }
    }
  }, []);
  
  // Fonction pour vérifier un jeton CSRF
  const validateToken = (token) => {
    return validateCSRFToken(token);
  };
  
  // Fonction pour rafraîchir le jeton CSRF si nécessaire
  const refreshToken = () => {
    const token = generateCSRFToken();
    setCsrfToken(token);
    return token;
  };
  
  // Fonction pour créer une instance axios sécurisée (uniquement pour login et pin)
  const createSecureAxios = () => {
    const secureAxiosInstance = axios.create();
    
    // Ajouter l'intercepteur CSRF uniquement à cette instance
    secureAxiosInstance.interceptors.request.use((config) => {
      // Pour les requêtes POST, ajouter le token CSRF aux données
      if (config.method === 'post' && config.data) {
        // Si les données sont déjà un URLSearchParams, ajoutez le token
        if (config.data instanceof URLSearchParams) {
          config.data.append('csrf_token', csrfToken);
        } else if (typeof config.data === 'string') {
          // Si c'est une chaîne URL-encodée, la convertir en URLSearchParams
          const searchParams = new URLSearchParams(config.data);
          searchParams.append('csrf_token', csrfToken);
          config.data = searchParams.toString();
        } else if (typeof config.data === 'object') {
          // Si c'est un objet, ajouter le token
          config.data.csrf_token = csrfToken;
        }
      }
      
      // Ajouter aussi un en-tête X-CSRF-Token
      config.headers['X-CSRF-Token'] = csrfToken;
      return config;
    });
    
    return secureAxiosInstance;
  };
  
  // Valeur du contexte exposée aux composants
  const value = {
    csrfToken,
    validateToken,
    refreshToken,
    createSecureAxios,
    isAuthenticated,
    setIsAuthenticated,
    user,
    setUser
  };
  
  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

// Exporter le contexte pour l'utilisation directe
export { SecurityContext };